import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { IUiStyle, IUser } from '@boma/administration-portal/shared/entities';
import {
  getUiStyle,
  getUser,
  loadUser,
} from '@boma/administration-portal/shared/state';
import { Store } from '@ngrx/store';
import { OAuthService } from 'angular-oauth2-oidc';
import jwt_decode from 'jwt-decode';
import { PrimeNGConfig } from 'primeng/api';
import { Observable, Subject, Subscription } from 'rxjs';
import { environment } from '../environments/environment';

@Component({
  selector: 'boma-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'administration-portal';
  menuMode = 'static';
  colorScheme = 'light';
  menuTheme = 'layout-sidebar-darkgray';
  authSvcEventSubscription$: Subscription | undefined;
  username: string | null = null;
  overlayMenuActive: boolean;
  staticMenuDesktopInactive: boolean;
  staticMenuMobileActive: boolean;
  menuClick: boolean;
  search = false;
  searchClick = false;
  userMenuClick: boolean;
  topbarUserMenuActive: boolean;
  notificationMenuClick: boolean;
  topBarNotificationMenuActive: boolean;
  rightMenuClick: boolean;
  rightMenuActive: boolean;
  configActive: boolean;
  configClick: boolean;
  resetMenu: boolean;
  menuHoverActive = false;
  inputStyle = 'outlined';
  ripple: boolean = false;
  style$: Observable<IUiStyle>;
  user$: Observable<IUser>;
  menuItems: any = [
    {
      label: 'General',
      icon: 'pi pi-fw pi-briefcase',
      items: [
        {
          label: 'Dashboard',
          icon: 'pi pi-fw pi-home',
          routerLink: ['/dashboard'],
        },
        {
          label: 'Users',
          icon: 'pi pi-fw pi-id-card',
          routerLink: ['/', 'users'],
        },
        {
          label: 'Organizations',
          icon: 'pi pi-fw pi-users',
          routerLink: ['/', 'crm'],
        },
        {
          label: 'Roles',
          icon: 'pi pi-fw pi-lock',
          routerLink: ['/', 'users', 'roles'],
        },
        {
          label: 'Global Products',
          icon: 'pi pi-fw pi-shopping-cart',
          routerLink: ['/', 'products'],
        },
        {
          label: 'Local Products',
          icon: 'pi pi-fw pi-shopping-cart',
          routerLink: ['/', 'products', 'local'],
        },
        {
          label: 'Categories',
          icon: 'pi pi-fw pi-tags',
          routerLink: ['/', 'products', 'categories'],
        },
        {
          label: 'Departments',
          icon: 'pi pi-fw pi-th-large',
          routerLink: ['/', 'products', 'departments'],
        },
        {
          label: 'Brands',
          icon: 'pi pi-fw pi-apple',
          routerLink: ['/', 'products', 'brands'],
        },
        {
          label: 'Countries',
          icon: 'pi pi-fw pi-globe',
          routerLink: ['/', 'locations', 'countries'],
        },
      ],
    },
  ];

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private primengConfig: PrimeNGConfig,
    private store: Store,
    private router: Router,
    private authService: OAuthService
  ) {
    if (
      (window.location as any).protocol != 'https:' &&
      environment.enforceHttps
    ) {
      location.href = location.href.replace('http://', 'https://');
    }

    authService.configure({
      issuer: environment.authentication.issuer,
      redirectUri: `${window.location.origin}/login`,
      clientId: environment.authentication.clientId,
      responseType: 'code',
      scope: environment.authentication.scopes,
      showDebugInformation: !environment.production,
    });
    authService.loadDiscoveryDocumentAndTryLogin().then(
      (res) => {
        if (!authService.hasValidAccessToken()) authService.initLoginFlow();
        else {
          this.store.dispatch(loadUser());
          this.setUsername();
          if (this.router.url.startsWith('/login')) this.router.navigate(['/']);
        }
      },
      (err) => console.log(err)
    );
    authService.setupAutomaticSilentRefresh();
  }

  ngOnInit(): void {
    this.style$ = this.store.select(getUiStyle);

    this.user$ = this.store.select(getUser);

    this.authSvcEventSubscription$ = this.authService.events.subscribe((ev) => {
      if (ev.type === 'token_received') {
        this.setUsername();
        this.store.dispatch(loadUser());
      } else if (ev.type === 'logout') {
        this.setUsername();
      }
    });

    const ctx = this;
    this.style$.subscribe({
      next(res) {
        if (res != null) {
          (ctx.menuMode = res.menuMode !== '' ? res.menuMode : ctx.menuMode),
            (ctx.colorScheme =
              res.colorScheme !== '' ? res.colorScheme : ctx.colorScheme);
          ctx.menuTheme =
            res.menuTheme !== ''
              ? 'layout-sidebar-' + res.menuTheme
              : ctx.menuTheme;

          ctx.ripple = res.ripple != null ? res.ripple : ctx.ripple;

          ctx.changeStyleSheetsColor(
            'theme-css',
            'theme-' + ctx.colorScheme + '.css',
            res.componentTheme !== '' ? res.componentTheme : 'blue'
          );

          if (res.colorScheme !== '') {
            ctx.changeStyleSheetsColor(
              'layout-css',
              'layout-' + res.colorScheme + '.css',
              null
            );

            const mobileLogoLink: HTMLImageElement = document.getElementById(
              'logo-mobile'
            ) as HTMLImageElement;
            const footerLogoLink: HTMLImageElement = document.getElementById(
              'footer-logo'
            ) as HTMLImageElement;
            const appLogoLink: HTMLImageElement = document.getElementById(
              'app-logo'
            ) as HTMLImageElement;

            if (mobileLogoLink) {
              if (res.colorScheme === 'light') {
                mobileLogoLink.src =
                  'assets/layout/images/logo-dark-text-2021.svg';
                footerLogoLink.src =
                  'assets/layout/images/logo-dark-text-2021.svg';
                if (res.menuTheme == 'white') {
                  appLogoLink.src =
                    'assets/layout/images/logo-dark-text-2021.svg';
                } else {
                  appLogoLink.src =
                    'assets/layout/images/logo-light-text-2021.svg';
                }
              } else {
                mobileLogoLink.src =
                  'assets/layout/images/logo-light-text-2021.svg';
                footerLogoLink.src =
                  'assets/layout/images/logo-light-text-2021.svg';
                appLogoLink.src =
                  'assets/layout/images/logo-light-text-2021.svg';
              }
            }
          }
        }
      },
    });
    this.primengConfig.ripple = true;
  }

  changeStyleSheetsColor(id, value, sub) {
    const element = document.getElementById(id);
    const urlTokens = element.getAttribute('href').split('/');

    urlTokens[urlTokens.length - 1] = value;
    if (sub != null) {
      // we have a sub.. add it
      urlTokens[urlTokens.length - 2] = sub;
    }
    const newURL = urlTokens.join('/');
    this.replaceLink(element, newURL);
  }

  replaceLink(linkElement, href) {
    if (this.isIE()) {
      linkElement.setAttribute('href', href);
    } else {
      const id = linkElement.getAttribute('id');
      const cloneLinkElement = linkElement.cloneNode(true);

      cloneLinkElement.setAttribute('href', href);
      cloneLinkElement.setAttribute('id', id + '-clone');

      linkElement.parentNode.insertBefore(
        cloneLinkElement,
        linkElement.nextSibling
      );

      cloneLinkElement.addEventListener('load', () => {
        linkElement.remove();
        cloneLinkElement.setAttribute('id', id);
      });
    }
  }

  isIE() {
    return /(MSIE|Trident\/|Edge\/)/i.test(window.navigator.userAgent);
  }

  onLayoutClick() {
    if (!this.searchClick) {
      this.search = false;
    }

    if (!this.userMenuClick) {
      this.topbarUserMenuActive = false;
    }

    if (!this.notificationMenuClick) {
      this.topBarNotificationMenuActive = false;
    }

    if (!this.rightMenuClick) {
      this.rightMenuActive = false;
    }

    if (!this.menuClick) {
      if (this.isSlim()) {
        // this.menuService.reset();
      }

      if (this.overlayMenuActive || this.staticMenuMobileActive) {
        this.hideOverlayMenu();
      }

      this.menuHoverActive = false;
      this.unblockBodyScroll();
    }

    if (this.configActive && !this.configClick) {
      this.configActive = false;
    }

    this.searchClick = false;
    this.configClick = false;
    this.userMenuClick = false;
    this.rightMenuClick = false;
    this.notificationMenuClick = false;
    this.menuClick = false;
  }

  onMenuButtonClick(event) {
    this.menuClick = true;
    this.topbarUserMenuActive = false;
    this.topBarNotificationMenuActive = false;
    this.rightMenuActive = false;

    if (this.isOverlay()) {
      this.overlayMenuActive = !this.overlayMenuActive;
    }

    if (this.isDesktop()) {
      this.staticMenuDesktopInactive = !this.staticMenuDesktopInactive;
    } else {
      this.staticMenuMobileActive = !this.staticMenuMobileActive;
      if (this.staticMenuMobileActive) {
        this.blockBodyScroll();
      } else {
        this.unblockBodyScroll();
      }
    }

    event.preventDefault();
  }

  onSearchClick(event) {
    this.search = !this.search;
    this.searchClick = !this.searchClick;
  }

  onMenuClick() {
    this.menuClick = true;
    this.resetMenu = false;
  }

  onTopbarUserMenuButtonClick(event) {
    this.userMenuClick = true;
    this.topbarUserMenuActive = !this.topbarUserMenuActive;

    this.hideOverlayMenu();

    event.preventDefault();
  }

  onTopbarNotificationMenuButtonClick(event) {
    this.notificationMenuClick = true;
    this.topBarNotificationMenuActive = !this.topBarNotificationMenuActive;

    this.hideOverlayMenu();

    event.preventDefault();
  }

  onRightMenuClick(event) {
    this.rightMenuClick = true;
    this.rightMenuActive = !this.rightMenuActive;

    this.hideOverlayMenu();

    event.preventDefault();
  }

  onRippleChange(event) {
    this.ripple = event.checked;
  }

  onConfigClick(event) {
    this.configClick = true;
  }

  isSlim() {
    return this.menuMode === 'slim';
  }

  isOverlay() {
    return this.menuMode === 'overlay';
  }

  isDesktop() {
    return window.innerWidth > 991;
  }

  isMobile() {
    return window.innerWidth <= 991;
  }

  hideOverlayMenu() {
    this.overlayMenuActive = false;
    this.staticMenuMobileActive = false;
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(
          '(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)',
          'gi'
        ),
        ' '
      );
    }
  }

  isMenuHoverActive() {
    return this.menuHoverActive;
  }

  onSetMenuHoverActive() {
    this.menuHoverActive = !this.menuHoverActive;
  }

  onSetMenuHoverActiveFalse() {
    this.menuHoverActive = false;
  }

  onSetStaticMenuMobileActiveFalse() {
    this.staticMenuMobileActive = false;
  }

  ngOnDestroy(): void {
    this._destroying$.next(null);
    this._destroying$.complete();
  }

  private async setUsername() {
    var hasValidAccessToken = this.authService.hasValidAccessToken();
    if (hasValidAccessToken) {
      var token = jwt_decode(this.authService.getAccessToken()) as any;
      this.username = `${token.given_name} ${token.family_name}`;
    } else {
      this.username = null;
    }
  }
}
